/**
 * Pictures that failed, asked for again once asking can work.
 *
 * The app opens straight into the chat for someone this phone has let in
 * before, on any signal, without waiting for the photo session to be
 * renewed. A photo already on the phone shows regardless -- the service
 * worker answers from its own copy -- but one that is not is refused by the
 * gateway until the session is back, and an <img> that has failed never
 * asks twice. So every failure from the gateway is noted, and asked for
 * again when the session opens or the connection comes back.
 *
 * Errors do not bubble, so this listens in the capture phase, once, for the
 * whole document rather than on each of the dozen places a picture is drawn.
 */

type Media = HTMLImageElement | HTMLVideoElement;

const failed = new Set<Media>();

function sourceOf(el: Media): string {
  return el instanceof HTMLVideoElement ? el.currentSrc || el.src : el.src;
}

/**
 * Starts noting failures from the gateway. Returns the way to stop.
 *
 * `onBroken` hears of each one as it happens, which is how a lapsed photo
 * session gets renewed from any screen. Only chat used to: a GiviSnap posted
 * on a phone whose cookie had run out while the app sat in the background
 * unfrosted a feed of pictures that were all refused, and stayed blank until
 * a reload opened a new session.
 */
export function watchBrokenMedia(
  gateway: string,
  root: Document = document,
  onBroken?: () => void,
): () => void {
  const noted = (event: Event) => {
    const el = event.target;
    if (!(el instanceof HTMLImageElement || el instanceof HTMLVideoElement)) return;
    if (!gateway || !sourceOf(el).startsWith(gateway)) return;
    failed.add(el);
    onBroken?.();
  };
  const back = () => retryBrokenMedia();
  root.addEventListener('error', noted, true);
  root.defaultView?.addEventListener('online', back);
  return () => {
    root.removeEventListener('error', noted, true);
    root.defaultView?.removeEventListener('online', back);
    failed.clear();
  };
}

/**
 * Asks again for everything that failed and is still on screen. Taken off the
 * list as it goes, so a picture that fails again is noted afresh and asked
 * for the next time, rather than in a loop.
 */
export function retryBrokenMedia(): number {
  let asked = 0;
  for (const el of [...failed]) {
    failed.delete(el);
    if (!el.isConnected) continue;
    if (el instanceof HTMLVideoElement) {
      el.load();
    } else {
      // Taken off and put back, which a browser treats as a new picture to
      // fetch; setting the same address again it may treat as nothing at all.
      const src = el.getAttribute('src');
      if (src === null) continue;
      el.removeAttribute('src');
      el.setAttribute('src', src);
    }
    asked += 1;
  }
  return asked;
}

/** How many are waiting to be asked for again. For tests. */
export function brokenCount(): number {
  return failed.size;
}
